/**
 * Map chrome (container / layer toggle / label filter / pin markers / mobile hamburger).
 * InfoWindow / marker editor / viewer / slideshow: see markerEditorShell,
 * markerViewerShell, mediaShell, and styles/thirdParty/googleMapsInfoWindow.css.
 *
 * Mobile control swap (single cutoff: 440px):
 * - layer toggle + desktop filter: hidden at max-[440px]
 * - hamburger: shown at max-[440px]
 *
 * Marker footprint: 44px phone → 48px tablet+ (bp-md). No Pro scale-up.
 */

import { chromeShell } from "./chromeShell";
import { typeShell } from "./typeShell";

/** Fallback px for imperative SVG attrs; responsive size comes from CSS classes. */
export const MAP_MARKER_SIZE_PX = 48;

const { scrollThin, filterBadge } = chromeShell;

/** Shared footprint for pin / thumbnail / cluster */
const markerFootprint =
    "h-11 w-11 bp-md:h-12 bp-md:w-12";

/** TOP_RIGHT desktop controls (layer + filter) share chrome */
const desktopControl =
    "m-0 flex h-11 min-w-36 cursor-pointer select-none items-center gap-3 rounded-lg border-2 border-solid border-border-light bg-surface px-4 py-3 font-sans text-sm font-medium text-text-primary shadow-none transition-all duration-200 ease-in-out hover:border-border hover:bg-surface-muted hover:shadow-none max-[440px]:!hidden bp-lg:min-w-[6.5rem] bp-lg:gap-1.5 bp-lg:px-3 bp-lg:py-1.5 bp-lg:text-xs";

export const mapChromeShell = {
    /* ——— Map container ——— */
    wrapper:
        "relative box-border h-[calc(100vh-var(--header-height,56px))] w-full overflow-hidden bg-surface-muted dark:bg-surface-inverse [@media(min-width:1024px)_and_(max-height:768px)]:h-[calc(100vh-var(--header-height,48px))]",
    map: "h-full w-full overflow-hidden",

    /* ——— Layer toggle (TOP_RIGHT Google Maps control) ——— */
    layerToggleContainer:
        "flex flex-col items-end max-[440px]:!hidden bp-lg:m-3 bp-xl:m-3 bp-2xl:m-4",
    layerToggle: desktopControl,
    layerToggleActive:
        "border-button-primary bg-button-primary text-text-inverse hover:border-button-primary-hover hover:bg-button-primary-hover",
    layerToggleIcon:
        "material-symbols-outlined shrink-0 text-icon-control text-text-muted transition-colors duration-200 ease-in-out [font-variation-settings:'FILL'_0,'wght'_400,'GRAD'_0,'opsz'_40]",
    layerToggleIconActive: "text-text-inverse",
    layerToggleLabel: typeShell.chromeLabel,

    /* ——— Label filter (TOP_RIGHT; desktop) ——— */
    filterControlContainer:
        "relative ml-auto flex flex-col items-end max-[440px]:!hidden",
    filterTrigger: desktopControl,
    filterTriggerOpen: "border-border bg-surface-muted",
    filterTriggerIcon: "material-icons shrink-0 text-icon-control",
    filterTriggerLabel:
        `min-w-0 flex-1 text-left ${typeShell.chromeLabel}`,
    filterTriggerBadge: filterBadge,
    filterTriggerBadgeActive: "bg-button-primary text-text-inverse",
    /** 全部表示（「全て」）用。絞り込み中より控えめに見せる */
    filterTriggerBadgeNeutral: "bg-surface-muted text-text-secondary",
    filterTriggerBadgeIdle: "invisible",
    filterPanel:
        "absolute right-0 top-full z-[1000] mt-2 max-h-[25rem] w-full min-w-[16rem] max-w-xs animate-filter-menu-in overflow-hidden rounded-lg border border-solid border-border bg-surface",
    filterPanelHeader:
        "flex items-center justify-between border-0 border-b border-solid border-border-subtle bg-surface-muted px-3 py-3 bp-2xl:px-4 bp-2xl:py-3",
    filterPanelTitle: `m-0 ${typeShell.bodySemibold}`,
    filterPanelClose:
        "flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-text-secondary transition-colors hover:bg-border-subtle hover:text-text-primary",
    filterPanelCloseIcon: "material-icons text-icon-control",
    filterPanelList: `max-h-[18.75rem] overflow-y-auto px-2 py-2 bp-xl:max-h-[20rem] bp-2xl:max-h-[21.875rem] ${scrollThin}`,
    filterPanelItem:
        "mb-0.5 flex cursor-pointer items-center rounded-md px-2 py-2 transition-colors hover:bg-surface-muted bp-2xl:px-3 bp-2xl:py-2.5",
    filterPanelCheckbox:
        "mr-2 size-4 shrink-0 cursor-pointer accent-button-primary",
    filterPanelSwatch:
        "mr-2 size-4 shrink-0 rounded-full border border-solid border-black/10",
    filterPanelItemLabel: `flex-1 ${typeShell.bodyMedium}`,
    filterPanelStatus: `m-0 px-3 py-3 italic ${typeShell.hint}`,
    filterPanelError: `m-0 px-3 py-3 ${typeShell.error}`,

    /* ——— Pin / cluster markers ——— */
    markerItem:
        "will-change-[transform,opacity] transition-transform duration-200 ease hover:scale-105",
    markerVisible: "animate-marker-drop opacity-100 bp-md:animate-marker-drop-md",
    thumbnailContainer:
        "relative inline-block animate-thumbnail-bounce bp-md:animate-thumbnail-bounce-md",
    pinContainer: "relative inline-block animate-pin-drop",
    pinSvg: `block ${markerFootprint}`,
    thumbnail: `box-border ${markerFootprint} rounded-lg border-2 border-solid border-text-strong object-cover`,
    /** Container chrome for React ThumbnailOnViewer (same footprint as pin) */
    thumbnailMarker: `relative flex ${markerFootprint} overflow-hidden rounded-lg border-2 border-solid border-text-strong bg-surface-muted shadow-none transition-[transform,border-color] hover:-translate-y-px hover:border-text-strong hover:shadow-none [&:hover_.thumbnail-image]:scale-[1.02]`,
    loading:
        `absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded bg-surface/80 px-1 py-0.5 bp-md:px-1.5 bp-md:py-1 ${typeShell.empty}`,
    clusterBadge: `flex ${markerFootprint} cursor-pointer items-center justify-center rounded-full border-2 border-solid border-text-inverse bg-surface-inverse text-sm font-semibold text-text-inverse shadow-none transition-[transform,background-color] duration-150 ease hover:scale-110 hover:bg-surface-inverse`,

    /* ——— Mobile hamburger (TOP_LEFT; ≤440px only) ——— */
    hamburgerControlContainer: "relative z-[1000] m-2 hidden max-[440px]:block",
    hamburgerMenu: "relative z-[1000] hidden max-[440px]:block",
    hamburgerButton:
        "relative flex h-auto min-h-14 w-auto min-w-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-solid border-border-light bg-surface p-2 shadow-none transition-all duration-200 ease hover:scale-[1.02] hover:bg-surface-muted",
    hamburgerButtonActive: "border-button-primary bg-button-primary",
    hamburgerButtonIcon: "material-symbols-outlined text-icon-control text-text-secondary",
    hamburgerButtonIconActive: "text-text-inverse",
    hamburgerButtonLabel:
        "m-0 whitespace-nowrap text-center text-sm font-medium leading-none text-text-secondary",
    hamburgerButtonLabelActive: "text-text-inverse",
    hamburgerBadge:
        "absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-xl border-2 border-solid border-surface px-1.5 py-1 text-2xs font-semibold shadow-none",
    hamburgerBadgeNeutral: "bg-surface-muted text-text-secondary",
    hamburgerBadgeActive: "bg-button-danger text-text-inverse",
    hamburgerBackdrop:
        "fixed inset-0 z-[1000] animate-hamburger-fade-in bg-black/40 backdrop-blur-[2px]",
    hamburgerPanel:
        "fixed left-2 right-2 top-40 z-[1001] max-h-[calc(100vh-6rem)] w-[calc(100vw-1rem)] min-w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] touch-pan-y animate-hamburger-slide-up overflow-hidden rounded-t-2xl border border-b-0 border-solid border-border-light bg-surface shadow-none transition-transform duration-200 ease-out",
    hamburgerHeader:
        "relative flex items-center justify-between border-0 border-b border-solid border-border-light bg-surface-muted p-2 pt-3",
    hamburgerDragHandle:
        "absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 cursor-grab rounded-sm bg-ui-muted active:cursor-grabbing",
    hamburgerTitle: "m-0 text-base font-semibold text-text-primary",
    hamburgerClose:
        "flex size-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-text-secondary transition-all duration-200 ease hover:bg-surface-muted hover:text-text-secondary",
    hamburgerCloseIcon: "material-symbols-outlined text-icon-control",
    hamburgerTabs: "flex border-0 border-b border-solid border-border-light",
    hamburgerTab:
        "flex-1 cursor-pointer border-0 border-b-2 border-solid border-transparent bg-transparent p-2 text-sm font-medium text-text-secondary transition-all duration-200 ease hover:bg-surface-muted hover:text-text-secondary",
    hamburgerTabActive: "border-button-primary bg-accent-10 text-button-primary",
    hamburgerContent: `max-h-[calc(100vh-12rem)] w-full overflow-y-auto ${scrollThin}`,

    filterContent: "h-full overflow-y-auto p-3",
    filterSearch:
        "relative mb-4 flex items-center gap-2 rounded-lg border border-solid border-border-light bg-surface-muted px-3 py-2",
    filterSearchIcon: "material-symbols-outlined shrink-0 text-icon-control text-text-hint",
    filterSearchInput:
        `flex-1 border-none bg-transparent p-0 text-text-secondary outline-none placeholder:text-text-hint ${typeShell.body}`,
    filterSearchClear:
        "flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-text-hint transition-all duration-200 ease hover:bg-border-subtle hover:text-text-secondary",
    filterSearchClearIcon: "material-symbols-outlined text-icon-inline",
    filterEmpty: `px-4 py-8 text-center ${typeShell.empty}`,
    filterHeader:
        "mb-4 flex items-center justify-between border-0 border-b border-solid border-border-light pb-3",
    filterToggleAll:
        "cursor-pointer rounded-md border-none bg-button-primary px-3 py-2 text-xs font-medium text-text-inverse transition-all duration-200 ease hover:bg-button-primary-hover",
    filterCount: typeShell.captionMuted,
    filterList: `flex max-h-[calc(100vh-16rem)] w-full flex-col gap-2 overflow-y-auto ${scrollThin}`,
    filterItem:
        "flex cursor-pointer items-center gap-3 rounded-lg border border-solid border-surface-muted bg-surface-muted p-3 transition-all duration-200 ease hover:border-border-light hover:bg-surface-muted",
    filterCheckbox: "size-4 accent-button-primary",
    filterColor: "size-4 shrink-0 rounded-full border border-solid border-border-light",
    filterName: `flex-1 ${typeShell.muted}`,

    layerContent: "h-full w-full overflow-y-auto p-3",
    layerItem: "mb-3",
    layerToggleRow:
        "flex cursor-pointer items-center gap-3 rounded-lg border border-solid border-surface-muted bg-surface-muted p-3 transition-all duration-200 ease hover:border-border-light hover:bg-surface-muted",
    layerCheckbox: "size-4 accent-button-primary",
    layerName: `flex-1 ${typeShell.muted}`,

    mobileLoading: `p-4 text-center ${typeShell.hint}`,
    mobileError: `p-4 text-center ${typeShell.error}`,
};
